// What the knowledge bank holds for a goal (knowledge-bank.md §11.5, §11.9;
// FR-13.9), shown inside the goal's views (GoalViews):
//
//   GoalKnowledge      the goal's "Knowledge" section: its claims, each with
//                      its source reference and the asks to the agent.
//   DefinitionBacking  a term's agreed definition: the claims it rests on, or
//                      the unsupported mark when no live claim backs it.
//
// Pure projections, intents out.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import {
    type Claim,
    type ClaimIntents,
    ClaimList,
} from "../../../components/ClaimRow/ClaimRow.js";
import { Icon } from "../../../components/Icon/Icon.js";
import { Text } from "../../../components/Text/Text.js";
import type { GoalTerm } from "./goal.js";

export const GoalKnowledge = ({
    claims,
    intents,
}: {
    readonly claims: readonly Claim[];
    readonly intents: ClaimIntents;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <>
            <div className="bilda-goal-sub">
                <h4 className="bilda-goal-flush">
                    <Text as="span" size="xs" weight="semibold">
                        {t`Knowledge`}
                    </Text>
                </h4>
            </div>
            <ClaimList
                claims={claims}
                intents={intents}
                empty={t`No claims in the knowledge bank for this goal yet.`}
            />
        </>
    );
};

export const DefinitionBacking = ({
    term,
    claims,
    intents,
}: {
    readonly term: GoalTerm;
    // The goal's claims; the definition's are picked from them.
    readonly claims: readonly Claim[];
    readonly intents: ClaimIntents;
}): ReactNode => {
    const { t } = useLingui();
    if (term.definition === undefined) return null;
    if (term.definitionClaims.length === 0)
        return (
            <Text as="p" size="xs" tone="dim" className="bilda-goal-flush">
                <Icon name="warning" size="xs" /> {t`Unsupported`}
            </Text>
        );
    return (
        <ClaimList
            claims={claims.filter((c) =>
                term.definitionClaims.includes(c.claimId),
            )}
            intents={intents}
        />
    );
};
